import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Zap, HelpCircle, Loader2, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { supabase } from "@/integrations/supabase/client";
import { getSupabaseAuthHeaders } from "@/lib/supabaseAuth";
import { toast } from "sonner";

// Stripe price and product IDs mapping
const STRIPE_TIERS = {
  starter: {
    price_id: "price_1SaqAA5S9nnmI3T0tQm6J7Yy",
    product_id: "prod_TXw2zbIcWziifH",
  },
  professional: {
    price_id: "price_1SaqAS5S9nnmI3T0EiiNiiYL",
    product_id: "prod_TXw218UdkpfROa",
  },
  business: {
    price_id: "price_1SaqAl5S9nnmI3T0uZwXvFGB",
    product_id: "prod_TXw2sEsDlC3ABl",
  },
};

const plans = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    description: "Perfect for personal use and trying out MeetHub",
    popular: false,
    cta: "Get Started",
    tier: "free",
    features: [
      "Up to 100 participants per meeting",
      "40-minute meeting limit",
      "HD video & audio quality",
      "Screen sharing",
      "Real-time chat",
      "Virtual backgrounds",
      "Guest access (no account needed)",
      "Basic meeting security",
    ],
  },
  {
    name: "Starter",
    price: "$8",
    period: "per user/month",
    description: "Great for small teams and freelancers",
    popular: false,
    cta: "Start Free Trial",
    tier: "starter",
    features: [
      "Up to 150 participants",
      "Unlimited meeting duration",
      "Cloud recording (5 hours)",
      "Waiting room & passwords",
      "Polls & Q&A",
      "Calendar integration",
      "Email support",
      "Everything in Free",
    ],
  },
  {
    name: "Professional",
    price: "$15",
    period: "per user/month",
    description: "Most popular for growing teams",
    popular: true,
    cta: "Start Free Trial",
    tier: "professional",
    features: [
      "Up to 300 participants",
      "Cloud recording (20 hours)",
      "Breakout rooms",
      "Live captions & transcription",
      "Meeting analytics",
      "Custom branding",
      "Priority support",
      "Everything in Starter",
    ],
  },
  {
    name: "Business",
    price: "$25",
    period: "per user/month",
    description: "For larger teams and organizations",
    popular: false,
    cta: "Start Free Trial",
    tier: "business",
    features: [
      "Up to 500 participants",
      "Unlimited cloud recording",
      "Live streaming (YouTube, Facebook)",
      "Advanced analytics dashboard",
      "Admin controls & user roles",
      "SSO integration",
      "API access",
      "Dedicated account manager",
    ],
  },
];

const faqs = [
  {
    question: "Can I try MeetHub before committing to a paid plan?",
    answer: "Absolutely! Our Free plan lets you experience MeetHub's core features with no time limit. All paid plans also come with a 14-day free trial so you can explore premium features risk-free.",
  },
  {
    question: "Can I change or cancel my plan anytime?",
    answer: "Yes! You can upgrade, downgrade, or cancel your subscription at any time. If you upgrade, changes take effect immediately. If you downgrade or cancel, you'll retain access to your current plan until the end of your billing period.",
  },
  {
    question: "Do you offer discounts for annual billing?",
    answer: "Yes, save 20% when you choose annual billing on any paid plan. We also offer special discounts for non-profits, educational institutions, and startups. Contact our sales team for details.",
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept all major credit cards (Visa, Mastercard, American Express), PayPal, and wire transfers for Enterprise plans. All payments are processed securely through Stripe.",
  },
  {
    question: "Is my data secure with MeetHub?",
    answer: "Security is our top priority. All meetings are protected with end-to-end encryption, and we're SOC 2 Type II certified. We comply with GDPR, CCPA, and HIPAA requirements for healthcare organizations.",
  },
  {
    question: "Do guests need an account to join meetings?",
    answer: "No! Anyone can join a MeetHub meeting as a guest without creating an account. Simply share the meeting link or code, and they can join instantly from any browser.",
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [subscription, setSubscription] = useState<{
    subscribed: boolean;
    product_id: string | null;
    subscription_end: string | null;
  } | null>(null);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      if (user) {
        await checkSubscription();
      } else {
        setCheckingSubscription(false);
      }
    };
    checkAuth();

    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        await checkSubscription();
      } else {
        setSubscription(null);
        setCheckingSubscription(false);
      }
    });

    return () => authSub.unsubscribe();
  }, []);

  const checkSubscription = async () => {
    try {
      setCheckingSubscription(true);
      const authHeaders = await getSupabaseAuthHeaders();
      if (!authHeaders) {
        setSubscription(null);
        return;
      }

      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: authHeaders,
      });
      if (error) throw error;
      setSubscription(data);
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setCheckingSubscription(false);
    }
  };

  const getCurrentTier = (): string | null => {
    if (!subscription?.subscribed || !subscription.product_id) return null;
    for (const [tier, ids] of Object.entries(STRIPE_TIERS)) {
      if (ids.product_id === subscription.product_id) return tier;
    }
    return null;
  };

  const handleSubscribe = async (tier: string) => {
    if (tier === "free") {
      navigate("/auth");
      return;
    }

    if (!user) {
      toast.info("Please sign in to subscribe");
      navigate("/auth");
      return;
    }

    const stripeConfig = STRIPE_TIERS[tier as keyof typeof STRIPE_TIERS];
    if (!stripeConfig) return;

    try {
      setLoadingPlan(tier);
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId: stripeConfig.price_id }
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      toast.error(error.message || "Failed to start checkout");
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setLoadingPlan('manage');
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error: any) {
      console.error('Portal error:', error);
      toast.error(error.message || "Failed to open billing portal");
    } finally {
      setLoadingPlan(null);
    }
  };

  const currentTier = getCurrentTier();

  const getPlanButton = (plan: typeof plans[0]) => {
    const isCurrentPlan = currentTier === plan.tier || (plan.tier === "free" && !subscription?.subscribed);
    const isLoading = loadingPlan === plan.tier;

    if (isCurrentPlan && plan.tier !== "free") {
      return (
        <Button 
          className="w-full" 
          variant="outline"
          onClick={handleManageSubscription}
          disabled={loadingPlan === 'manage'}
        >
          {loadingPlan === 'manage' ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Settings className="h-4 w-4 mr-2" />
          )}
          Manage Subscription
        </Button>
      );
    }

    if (isCurrentPlan && plan.tier === "free") {
      return (
        <Button className="w-full" variant="outline" disabled>
          Current Plan
        </Button>
      );
    }

    return (
      <Button 
        className="w-full" 
        variant={plan.popular ? "default" : "outline"}
        onClick={() => handleSubscribe(plan.tier)}
        disabled={isLoading || checkingSubscription}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
        ) : null}
        {plan.cta}
      </Button>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <Navigation />
      <div className="container mx-auto px-4 py-8 pt-24">

        <div className="max-w-7xl mx-auto space-y-16">
          {/* Hero Section */}
          <div className="text-center space-y-4">
            <Badge variant="outline" className="mb-4">
              14-day free trial on all paid plans
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold">Simple, Transparent Pricing</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Choose the perfect plan for your team. Start free and scale as you grow. No hidden fees, cancel anytime.
            </p>
            {subscription?.subscribed && subscription.subscription_end && (
              <p className="text-sm text-muted-foreground">
                Your subscription renews on {new Date(subscription.subscription_end).toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => {
              const isCurrentPlan = currentTier === plan.tier || (plan.tier === "free" && !subscription?.subscribed);
              return (
                <Card 
                  key={index} 
                  className={`relative flex flex-col ${plan.popular ? 'border-primary shadow-lg ring-2 ring-primary/20' : ''} ${isCurrentPlan ? 'ring-2 ring-green-500/50 border-green-500' : ''}`}
                >
                  {plan.popular && !isCurrentPlan && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Zap className="h-3 w-3 mr-1" />
                      Most Popular
                    </Badge>
                  )}
                  {isCurrentPlan && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500">
                      <Check className="h-3 w-3 mr-1" />
                      Your Plan
                    </Badge>
                  )}
                  <CardHeader className="text-center pb-4">
                    <CardTitle className="text-xl mb-2">{plan.name}</CardTitle>
                    <div className="mb-2">
                      <span className="text-4xl font-bold">{plan.price}</span>
                      {plan.price !== "$0" && (
                        <span className="text-muted-foreground ml-1 text-sm">/ {plan.period}</span>
                      )}
                    </div>
                    <CardDescription className="text-sm">{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col space-y-4">
                    {getPlanButton(plan)}
                    <div className="space-y-3 flex-1">
                      {plan.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Enterprise Section */}
          <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-2">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl mb-2">Enterprise</CardTitle>
              <CardDescription className="text-lg">
                Custom solutions for large organizations with advanced requirements
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="grid md:grid-cols-4 gap-6 text-center">
                <div>
                  <h3 className="font-semibold mb-2">Unlimited Scale</h3>
                  <p className="text-sm text-muted-foreground">
                    1000+ participants with dedicated infrastructure
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Advanced Security</h3>
                  <p className="text-sm text-muted-foreground">
                    SSO, SAML, custom compliance & data residency
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Premium Support</h3>
                  <p className="text-sm text-muted-foreground">
                    24/7 dedicated support with SLA guarantee
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Custom Integration</h3>
                  <p className="text-sm text-muted-foreground">
                    API access, webhooks & custom development
                  </p>
                </div>
              </div>
              <div className="flex justify-center">
                <Button size="lg" onClick={() => navigate("/contact")}>
                  Contact Sales
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Common Features */}
          <div className="text-center space-y-8">
            <h2 className="text-3xl font-bold">Included in All Plans</h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="space-y-2">
                <h3 className="font-semibold">End-to-End Encryption</h3>
                <p className="text-sm text-muted-foreground">
                  All meetings are secured with industry-standard encryption
                </p>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold">Cross-Platform Support</h3>
                <p className="text-sm text-muted-foreground">
                  Works on web, desktop, iOS, and Android devices
                </p>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold">99.9% Uptime SLA</h3>
                <p className="text-sm text-muted-foreground">
                  Enterprise-grade reliability for your meetings
                </p>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-2">Frequently Asked Questions</h2>
              <p className="text-muted-foreground">
                Got questions? We've got answers.
              </p>
            </div>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="h-4 w-4 text-primary flex-shrink-0" />
                      {faq.question}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* CTA Section */}
          <div className="text-center space-y-4 py-8">
            <h2 className="text-3xl font-bold">Ready to Get Started?</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Join thousands of teams already using MeetHub for their video conferencing needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => navigate("/auth")}>
                Start Free Today
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/contact")}>
                Talk to Sales
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
